import { useState } from 'react'
import { useTranslation } from 'next-i18next'
import styled from 'styled-components'

import { Session } from '@/models/session'
import { colors } from '@/styles'
import formatName from '@/utils/format-name'
import PhoneFormatter from '@/components/phone-formatter'
import { Label, ParagraphSmall } from '@/components/text'

import {
  FormCard,
  FormCardInner,
  FormCardInnerRow,
  FormTitle
} from './elements'

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const TextButton = styled.button`
  background: none;
  border: none;
  color: ${colors.link};
`

type AccountSectionViewProps = {
  profile: Session
}

const AccountSectionView: React.FC<AccountSectionViewProps> = ({ profile }) => {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  return (
    <>
      <FormTitle text={t('b.auth.view.account_info.title', 'Account Info')} />
      <FormCard gapY="16px">
        <Header>
          <Label fontSize="16px" type="large">
            {expanded ? 'My Account' : formatName(profile)}
          </Label>
          <TextButton
            type="button"
            aria-expanded={expanded}
            aria-controls="account-info-section"
            onClick={() => setExpanded(value => !value)}
          >
            <Label fontSize="14px" type="medium">
              {expanded ? 'Close' : 'View'}
            </Label>
          </TextButton>
        </Header>
        <FormCardInner aria-hidden={!expanded} id="account-info-section">
          <FormCardInnerRow>
            <ParagraphSmall>
              {t('common.first_name', 'First name')}
            </ParagraphSmall>
            <ParagraphSmall>{profile.first_name || '-'}</ParagraphSmall>
          </FormCardInnerRow>
          <FormCardInnerRow>
            <ParagraphSmall>
              {t('common.last_name', 'Last name')}
            </ParagraphSmall>
            <ParagraphSmall>{profile.last_name || '-'}</ParagraphSmall>
          </FormCardInnerRow>
          <FormCardInnerRow>
            <ParagraphSmall>
              {t('b.auth.create.preferred_name.label', 'Preferred name')}
            </ParagraphSmall>
            <ParagraphSmall>{profile.preferred_name || '-'}</ParagraphSmall>
          </FormCardInnerRow>
          <FormCardInnerRow>
            <ParagraphSmall>{t('common.email', 'Email')}</ParagraphSmall>
            <ParagraphSmall>{profile.username || '-'}</ParagraphSmall>
          </FormCardInnerRow>
          <FormCardInnerRow>
            <ParagraphSmall>
              {t('b.auth.view.phone_number.placeholder', 'Phone number')}
            </ParagraphSmall>
            <ParagraphSmall>
              <PhoneFormatter
                value={profile.phone}
                countryCode={profile.phone_country_code_alpha ?? 'ca'}
                fallback="-"
              />
            </ParagraphSmall>
          </FormCardInnerRow>
        </FormCardInner>
      </FormCard>
    </>
  )
}

export default AccountSectionView
